import { Swiper, SwiperSlide, Navigation } from '@/components/ui/slider';
import { ArrowNext, ArrowPrev } from '@/components/icons';
import { Product } from '@/types';
import ProductCard from '@/components/products/cards/card';
import rangeMap from '@/lib/range-map';
import { PRODUCTS_PER_PAGE } from '@/framework/client/variables';
import { useRef, useMemo } from 'react';
import classNames from 'classnames';
import { useContainerWidth } from '@/lib/hooks/use-container-width';
import ProductCardLoader from '@/components/ui/loaders/product-card-loader';
import type { HomeListContext } from '@/lib/gtm';

const SWIPER_MODULES = [Navigation];

const ProductsSlider = ({
  uniqueKey = 'handPicked_products',
  products,
  isLoading = false,
  limit = PRODUCTS_PER_PAGE,
  listContext,
}: {
  uniqueKey?: string;
  products: Product[];
  isLoading?: boolean;
  limit?: number;
  listContext?: HomeListContext;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const nextElId = `${uniqueKey}-next`;
  const prevElId = `${uniqueKey}-prev`;

  const width = useContainerWidth(ref);

  const slidesPerView =
    width < 500 ? 1 : width < 700 ? 2 : width < 900 ? 3 : width < 1200 ? 4 : 5;

  const swiperStyle = useMemo(() => ({ padding: '12px 9px' }), []);

  const navigation = useMemo(
    () => ({
      nextEl: `#${nextElId}`,
      prevEl: `#${prevElId}`,
    }),
    [nextElId, prevElId],
  );

  const swiperId = useMemo(() => {
    const safe = uniqueKey.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 96);
    return `products-slider-${safe || 'default'}`;
  }, [uniqueKey]);

  return (
    <div className="relative">
      <div
        ref={ref}
        className={classNames(`relative lg:max-w-[1560px] mx-auto`)}
      >
        <Swiper
          id={swiperId}
          style={swiperStyle}
          slidesPerView={slidesPerView}
          spaceBetween={16}
          modules={SWIPER_MODULES}
          navigation={navigation}
        >
          {isLoading && !products.length
            ? rangeMap(limit, (i) => (
                <SwiperSlide key={i}>
                  <ProductCardLoader />
                </SwiperSlide>
              ))
            : null}

          {products?.map((product: Product) => (
            <SwiperSlide key={product?.id}>
              <ProductCard product={product} listContext={listContext} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          id={prevElId}
          className="absolute top-2/4 z-10 -mt-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-border-200 border-opacity-70 bg-light text-heading shadow-xl transition-all duration-200 hover:border-accent hover:bg-accent hover:text-light ltr:-left-4 rtl:-right-4 md:-mt-5 md:h-9 md:w-9 ltr:md:-left-5 rtl:md:-right-5"
          role="button"
        >
          <span className="sr-only">{"Anterior"}</span>
          <ArrowPrev width={18} height={18} />
        </div>
        <div
          id={nextElId}
          className="absolute top-2/4 z-10 -mt-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-border-200 border-opacity-70 bg-light text-heading shadow-xl transition-all duration-200 hover:border-accent hover:bg-accent hover:text-light ltr:-right-4 rtl:-left-4 md:-mt-5 md:h-9 md:w-9 ltr:md:-right-5"
          role="button"
        >
          <span className="sr-only">{"Próximo"}</span>
          <ArrowNext width={18} height={18} />
        </div>
      </div>
    </div>
  );
};

export default ProductsSlider;
